// src/components/Footer.tsx - Updated with modular color system and improved design
'use client';

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Skeleton,
} from '@mui/material';
import {
  Email,
  LinkedIn,
  Twitter,
  Facebook,
  YouTube,
  GitHub,
  Instagram,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import NextLink from 'next/link';

// Import API functions and types
import { api, type SiteSettings, type ResearchTheme } from '@/lib/api';

// Import our modular color system
import { withOpacity, darkTheme } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';

// Use the same brand gold the rest of the site uses for headers
// (brand.accent.dark = #E6C000). The uppercase + letter-spacing + heavy
// weight treatment below gives the headers enough perceived visual weight
// on the light footer surface even at imperfect colour contrast.
const FOOTER_HEADING_GOLD = '#E6C000';

const headingSx = {
  color: FOOTER_HEADING_GOLD,
  fontWeight: 800,
  fontSize: '0.85rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  mb: 1.5,
};

// Footer body follows the active theme (light by default). Only the bottom
// copyright/social bar is pinned dark — see CopyrightSection below.
const barColors = darkTheme;

const FooterContainer = styled(Box)<{ component?: React.ElementType }>(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.background.elevated,
    color: colors.text.primary,
    borderTop: `2px solid ${colors.brand.accent.main}`,
    marginTop: 'auto',
    fontSize: '0.85rem',
  };
});

const FooterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const FooterLink = styled(Link)<{ component?: React.ElementType }>(({ theme }) => {
  const colors = theme.colors;
  return {
    color: colors.text.secondary,
    textDecoration: 'none',
    display: 'block',
    marginBottom: '4px',
    transition: 'color 0.3s ease',
    fontSize: '0.85rem',
    '&:hover': {
      color: colors.brand.accent.dark,
      textDecoration: 'none',
    },
  };
});

// Social buttons sit on the dark CopyrightSection — pinned to barColors
// so they read clearly on the dark surface regardless of the active theme.
const SocialIconButton = styled(IconButton)({
  color: barColors.text.secondary,
  padding: 6,
  border: `1px solid ${withOpacity(barColors.brand.accent.main, 0.3)}`,
  margin: '0 2px',
  '&:hover': {
    color: barColors.brand.accent.main,
    borderColor: barColors.brand.accent.main,
    backgroundColor: withOpacity(barColors.brand.accent.main, 0.1),
  },
});

const CopyrightSection = styled(Box)({
  padding: '16px 32px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
  background: barColors.background.elevated,
  color: barColors.text.primary,
  borderTop: `1px solid ${withOpacity(barColors.brand.accent.main, 0.2)}`,
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    gap: 16,
  },
});

interface FooterProps {
  siteSettings?: SiteSettings;
}

const Footer: React.FC<FooterProps> = ({ siteSettings: propSiteSettings }) => {
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(propSiteSettings || null);
  const [researchThemes, setResearchThemes] = useState<ResearchTheme[]>([]);
  const [loading, setLoading] = useState(!propSiteSettings);

  const colors = useColorTheme();

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        setLoading(true);
        
        // Fetch data in parallel if not provided as props
        const promises = [];
        
        if (!propSiteSettings) {
          promises.push(api.getSiteSettings());
        }
        
        // Always fetch research themes for the footer
        promises.push(api.getResearchThemes());
        
        const results = await Promise.all(promises);
        
        let resultIndex = 0;
        
        if (!propSiteSettings) {
          setSiteSettings(results[resultIndex]);
          resultIndex++;
        }
        
        // Research themes are always the last result
        setResearchThemes(results[results.length - 1] || []);
        
        console.log('Footer data loaded:', {
          siteSettings: !propSiteSettings ? results[0] : propSiteSettings,
          researchThemes: results[results.length - 1],
          socialMediaLinks: (!propSiteSettings ? results[0] : propSiteSettings)?.socialMediaDefaults,
        });
        
      } catch (error) {
        console.error('Failed to fetch footer data:', error);
        // Set empty array as fallback
        setResearchThemes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFooterData();
  }, [propSiteSettings]);

  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'linkedin': return <LinkedIn fontSize="small" />;
      case 'twitter': return <Twitter fontSize="small" />;
      case 'facebook': return <Facebook fontSize="small" />;
      case 'youtube': return <YouTube fontSize="small" />;
      case 'instagram': return <Instagram fontSize="small" />;
      case 'github': return <GitHub fontSize="small" />;
      default: return <Email fontSize="small" />;
    }
  };

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'Research Themes', href: '/research-themes' },
    { label: 'Investigators', href: '/people/investigators' },
    { label: 'Students', href: '/people/students' },
  ];

  const moreLinks = [
    { label: 'News & Events', href: '/news-events' },
    { label: 'Resources', href: '/resources' },
    { label: 'Contact', href: '/contact' },
    { label: 'Advisory Board', href: '/governance/advisory-board' },
  ];

  // Contact info from API with fallbacks. Address compacted to 2 lines so
  // the Contact column doesn't make the whole footer row disproportionately
  // tall (the other columns end higher and the leftover gap was wasted).
  const contactInfo = {
    generalEmail: siteSettings?.contactEmail || 'srini@cs.dal.ca',
    address: 'Faculty of Computer Science, Dalhousie University\n6050 University Avenue, Halifax, NS B3H 4R2',
  };

  const actualSiteSettings = {
    siteName: siteSettings?.siteName || 'NSERC CREATE',
    copyrightText: siteSettings?.copyrightText || '© 2025 NSERC CREATE Cybersecurity Program, Dalhousie University',
    footerText: siteSettings?.footerText || 'This program is supported by the Natural Sciences and Engineering Research Council of Canada (NSERC) and our industry and government partners.',
  };

  // Social media links from API with fallbacks
  const socialLinks = siteSettings?.socialMediaDefaults?.length > 0 
    ? siteSettings.socialMediaDefaults.filter(link => link.isActive !== false) // Only filter out explicitly inactive links
    : [
        { platform: 'LinkedIn', url: 'https://www.linkedin.com/school/dalhousie-university/' },
        { platform: 'Twitter', url: 'https://twitter.com/DalCompSci' },
        { platform: 'YouTube', url: 'https://www.youtube.com/user/DalhousieUniv' },
      ];

  return (
    <FooterContainer component="footer">
      <Container maxWidth="lg">
        <Box sx={{ pt: 4, pb: 2 }}>
          <Grid container spacing={3}>
            {/* Quick Links */}
            <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
              <FooterSection>
                <Typography
                  variant="subtitle2"
                  sx={headingSx}
                >
                  Navigation
                </Typography>
                {quickLinks.map((link, index) => (
                  <FooterLink key={index} component={NextLink} href={link.href}>
                    {link.label}
                  </FooterLink>
                ))}
              </FooterSection>
            </Grid>

            {/* More Links */}
            <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
              <FooterSection>
                <Typography
                  variant="subtitle2"
                  sx={headingSx}
                >
                  More
                </Typography>
                {moreLinks.map((link, index) => (
                  <FooterLink key={index} component={NextLink} href={link.href}>
                    {link.label}
                  </FooterLink>
                ))}
              </FooterSection>
            </Grid>

            {/* Research Themes */}
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <FooterSection>
                <Typography
                  variant="subtitle2"
                  sx={headingSx}
                >
                  Research Themes
                </Typography>
                {loading ? (
                  <>
                    {Array.from({ length: 4 }, (_, index) => (
                      <Skeleton 
                        key={index} 
                        variant="text" 
                        height={20} 
                        width="90%" 
                        sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 0.5 }} 
                      />
                    ))}
                  </>
                ) : researchThemes.length > 0 ? (
                  <>
                    {researchThemes.slice(0, 6).map((theme) => (
                      <FooterLink 
                        key={theme.id} 
                        component={NextLink} 
                        href={`/research-themes#${theme.themeNumber?.toLowerCase()}`}
                      >
                        {theme.themeNumber}: {theme.title}
                      </FooterLink>
                    ))}
                    {researchThemes.length > 6 && (
                      <FooterLink 
                        component={NextLink} 
                        href="/research-themes" 
                        sx={{ color: colors.brand.accent.dark, fontWeight: 500 }}
                      >
                        View All Themes →
                      </FooterLink>
                    )}
                  </>
                ) : (
                  // Fallback themes if API fails
                  <>
                    <FooterLink component={NextLink} href="/research-themes">
                      T1: Advanced Cryptography
                    </FooterLink>
                    <FooterLink component={NextLink} href="/research-themes">
                      T2: AI-Powered Security
                    </FooterLink>
                    <FooterLink component={NextLink} href="/research-themes">
                      T3: IoT & Embedded Systems
                    </FooterLink>
                    <FooterLink component={NextLink} href="/research-themes">
                      T4: Critical Infrastructure
                    </FooterLink>
                    <FooterLink 
                      component={NextLink} 
                      href="/research-themes" 
                      sx={{ color: colors.brand.accent.dark, fontWeight: 500 }}
                    >
                      View All Themes →
                    </FooterLink>
                  </>
                )}
              </FooterSection>
            </Grid>

            {/* Contact Information */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FooterSection>
                <Typography
                  variant="subtitle2"
                  sx={headingSx}
                >
                  Contact Information
                </Typography>
                
                <Typography variant="body2" sx={{ color: FOOTER_HEADING_GOLD, fontWeight: 700 }}>
                  Dr. Srini Sampalli
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 500 }}>
                    Program Director, CREATE Program Professor
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 500, mb: 1 }}>
                     and 3M National Teaching Fellow
                </Typography> 

                <Typography variant="body2" sx={{ color: colors.text.secondary, fontWeight: 500 }}>
                  Email
                </Typography>
                <FooterLink href={`mailto:${contactInfo.generalEmail}`}>
                  {contactInfo.generalEmail}
                </FooterLink>

                <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 1.5, fontWeight: 500 }}>
                  Address
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: colors.text.disabled, 
                  whiteSpace: 'pre-line', 
                  lineHeight: 1.4,
                  fontSize: '0.8rem',
                }}>
                  {contactInfo.address}
                </Typography>
              </FooterSection>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Copyright and Social Media Section */}
      <CopyrightSection>
        {/* Social Media */}
        <Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {socialLinks.map((social, index) => (
              <SocialIconButton
                key={index}
                size="small"
                onClick={() => window.open(social.url, '_blank', 'noopener,noreferrer')}
                title={`Follow us on ${social.platform}`}
              >
                {getSocialIcon(social.platform)}
              </SocialIconButton>
            ))}
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: barColors.text.disabled, fontSize: '0.8rem' }}>
          {loading ? (
            <Skeleton variant="text" width={200} sx={{ bgcolor: withOpacity(barColors.text.primary, 0.1) }} />
          ) : (
            actualSiteSettings.copyrightText
          )}
        </Typography>
        
        {/* <Box sx={{ display: 'flex', gap: 2 }}>
          <FooterLink href="/privacy" sx={{ fontSize: '0.8rem' }}>Privacy Policy</FooterLink>
          <FooterLink href="/terms" sx={{ fontSize: '0.8rem' }}>Terms of Use</FooterLink>
          <FooterLink href="/accessibility" sx={{ fontSize: '0.8rem' }}>Accessibility</FooterLink>
        </Box> */}
      </CopyrightSection>
    </FooterContainer>
  );
};

export default Footer;